import i18next from "i18next";
import { useState } from "react";

import { Models, OrderPickupStatus, PackageType, ShopDisplayType } from "api/types";

import { isBeautifiPromoEnabled } from "./beautifiPromo";

export const filterItemsByProducts = <T extends { id: string }>(
  items: T[],
  productIds: string[],
): T[] => {
  return items.filter((item) => productIds.includes(item.id));
};

export const generateShopUrl = (view: string, params?: Record<string, string>) => {
  const searchParams = new URLSearchParams({ view, ...params });
  return `/products/list?${searchParams.toString()}`;
};

export const normalizeItemName = (item: {
  id: string;
  name?: string | null;
}): { id: string; name: string } => ({
  id: item.id,
  name: item.name ?? "",
});

export const getProductIds = (
  products: Models<"DisplayRetailProductListing">[],
  field: keyof Models<"DisplayRetailProductListing">,
): string[] => {
  return products.flatMap((product) => {
    const value = product[field];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }
    return typeof value === "string" ? [value] : [];
  });
};

export const isBestSeller = (
  product: Models<"DisplayRetailProductListing">,
  hidePricing: boolean,
) => {
  // "Members only" is a claim about who may buy, so it must follow the same rule as price
  // visibility: with anonymous checkout on, a signed-out visitor can buy and sees the real tag.
  if (hidePricing) {
    return i18next.t("retailProductsHelpers.membersOnly");
  } else if (product.isBestSeller) {
    return i18next.t("retailProductsHelpers.bestSeller");
  }

  return "";
};

export const isFeatured = (product: Models<"DisplayRetailProductListing">) => {
  return product.isFeaturedProduct ? i18next.t("retailProductsHelpers.featured") : "";
};

export const shouldShowAdonisCTA = (index: number, isAdonisEnabled: boolean): boolean => {
  return index % 4 === 3 && index % 8 === 3 && isAdonisEnabled;
};

export const shouldShowAffirmBanner = (
  index: number,
  dunningMembership: any,
  platform: any,
): boolean => {
  return (
    index % 4 === 3 &&
    index % 8 === 7 &&
    dunningMembership.status === false &&
    !!platform.platformSettings?.affirmEnabled
  );
};

export const shouldShowBeautifiBanner = (
  index: number,
  dunningMembership: any,
  platform: any,
): boolean => {
  return (
    index % 4 === 3 &&
    index % 8 === 7 &&
    dunningMembership.status === false &&
    isBeautifiPromoEnabled(platform?.platformSettings)
  );
};

export const useExpandedSections = (initialSection?: string | null) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    initialSection ? new Set([initialSection]) : new Set(),
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      newSet.has(sectionId) ? newSet.delete(sectionId) : newSet.add(sectionId);
      return newSet;
    });
  };

  return { expandedSections, toggleSection };
};

export interface ProgressInfo {
  current: number;
  max: number;
  statusText: string;
}

const FIVE_DAYS_IN_MS = 5 * 24 * 60 * 60 * 1000;

// TODO: Replace this to pull from aftership.
const isOrderWithin5Days = (createdOnUtc: string): boolean => {
  const now = new Date();
  const orderCreatedDate = new Date(createdOnUtc);
  const daysSinceOrder = now.getTime() - orderCreatedDate.getTime();
  return daysSinceOrder < FIVE_DAYS_IN_MS;
};

const hasUncompletedPickupItems = (
  pickupItems: Models<"DisplayRetailProductOrderLineItemForPickup">[],
): boolean => {
  return pickupItems.some(
    (lineItem) => (lineItem.status as OrderPickupStatus) !== OrderPickupStatus.PickedUp,
  );
};

const isUpcomingOrder = (order: Models<"DisplayRetailProductOrderFromPerson">): boolean => {
  const hasPickupItems = order.lineItemsForPickup.length > 0;
  const hasShippingItems = order.lineItemsForShipping.length > 0;
  const isMixedOrder = hasPickupItems && hasShippingItems;

  const hasUncompleted = hasUncompletedPickupItems(order.lineItemsForPickup);
  const isWithin5Days = isOrderWithin5Days(order.createdOnUtc);

  // Mixed order: show if there's an uncompleted pickup OR within 5 days
  if (isMixedOrder) {
    return hasUncompleted || isWithin5Days;
  }

  // Pickup-only: show only if item hasn't been picked up
  if (hasPickupItems) {
    return hasUncompleted;
  }

  // Delivery-only: show if within 5 days of order
  if (hasShippingItems) {
    return isWithin5Days;
  }

  return false;
};

export const getUpcomingOrders = (orders: Models<"DisplayRetailProductOrderFromPerson">[]) => {
  return orders.filter(isUpcomingOrder);
};

export const getPastOrders = (orders: Models<"DisplayRetailProductOrderFromPerson">[]) => {
  return orders.filter((order) => !isUpcomingOrder(order));
};

export const getPastOrderItems = (orders: Models<"DisplayRetailProductOrderFromPerson">[]) => {
  return orders
    .flatMap((order) => {
      const hasShippingItems = order.lineItemsForShipping.length > 0;
      const isWithin5Days = isOrderWithin5Days(order.createdOnUtc);

      const items = [];

      // Add completed pickup items
      items.push(
        ...order.lineItemsForPickup
          .filter(
            (lineItem) => (lineItem.status as OrderPickupStatus) === OrderPickupStatus.PickedUp,
          )
          .map((lineItem) => ({
            orderId: order.orderID,
            completionDate: lineItem.lastRedeemedOnUtc,
            lineItem,
            lastRedeemedOnUtc: lineItem.lastRedeemedOnUtc,
            estimatedDeliveryDateAtUtc: undefined,
            shipmentTrackingUrl: undefined,
          })),
      );

      // Add shipping items for delivery/mixed orders that are past 5 days
      if (hasShippingItems && !isWithin5Days) {
        items.push(
          ...order.lineItemsForShipping.map((lineItem) => ({
            orderId: order.orderID,
            completionDate: lineItem.estimatedDeliveryDateAtUtc,
            lineItem,
            lastRedeemedOnUtc: undefined,
            estimatedDeliveryDateAtUtc: lineItem.estimatedDeliveryDateAtUtc,
            shipmentTrackingUrl: lineItem.shipmentTrackingUrl,
          })),
        );
      }

      return items;
    })
    .sort((a, b) => {
      const dateA = a.completionDate ? new Date(a.completionDate).getTime() : 0;
      const dateB = b.completionDate ? new Date(b.completionDate).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
};

export const getPickupProgress = (
  pickupItems: Models<"DisplayRetailProductOrderLineItemForPickup">[],
): ProgressInfo => {
  if (pickupItems.length === 0) {
    return { current: 0, max: 2, statusText: i18next.t("retailProductsHelpers.noItems") };
  }

  const statusText = i18next.t("retailProductsHelpers.readyForYou");
  return {
    current: 2,
    max: 2,
    statusText,
  };
};

export const getDeliveryProgress = (
  shippingItems: Models<"DisplayRetailProductOrderLineItemForShipping">[],
): ProgressInfo => {
  if (shippingItems.length === 0) {
    return { current: 0, max: 2, statusText: i18next.t("retailProductsHelpers.noItems") };
  }

  // Determine status based on tracking URL availability
  const hasTrackingUrl = shippingItems[0].shipmentTrackingUrl != null;
  const statusInfo = hasTrackingUrl
    ? {
        current: 2,
        text: i18next.t("retailProductsHelpers.headedYourWay"),
      }
    : {
        current: 1,
        text: i18next.t("retailProductsHelpers.processingYourOrder"),
      };

  return {
    current: Math.max(statusInfo.current, 1),
    max: 2,
    statusText: statusInfo.text,
  };
};

export const isRetailProductsEnabled = (platformSettings: Models<"PlatformSettingModel1">) =>
  !!(platformSettings.myProductsEnabled || platformSettings.skinDropEnabled);

export const SKINDROP_REFUND_DAYS = 30;

export const getRefundEligibilityDays = (
  order: Models<"DisplayRetailProductOrderFromPerson">,
  storefrontRefundEligibilityDays?: number,
): number => {
  if (order.lineItemsForShipping.length > 0) {
    return SKINDROP_REFUND_DAYS;
  }
  return storefrontRefundEligibilityDays ?? SKINDROP_REFUND_DAYS;
};

export const hasProducts = (items: any[]) => items?.length > 0;

export const hasFeatured = (products: Models<"DisplayRetailProductListing">[]) =>
  products.some((product) => product.isFeaturedProduct);

export const hasBestSellers = (products: Models<"DisplayRetailProductListing">[]) =>
  products.some((product) => product.isBestSeller);

const NEW_ARRIVAL_WINDOW_DAYS = 30;

export const isNewArrival = (
  product: Pick<Models<"DisplayRetailProductListing">, "createdAtUtc">,
) => {
  const windowStart = new Date();
  windowStart.setDate(windowStart.getDate() - NEW_ARRIVAL_WINDOW_DAYS);
  return new Date(product.createdAtUtc) >= windowStart;
};

export const hasNewArrivals = (products: Models<"DisplayRetailProductListing">[]) =>
  products.some(isNewArrival);

export const getRedeemedEarnedProducts = (
  earnedProducts: Models<"DisplayRetailProductOrderLineItemForPickup">[],
) => {
  return earnedProducts
    .filter((product) => product.status === OrderPickupStatus.PickedUp)
    .map((product) => ({
      orderId: undefined, // No order ID for earned products
      completionDate: product.lastRedeemedOnUtc,
      lineItem: product,
      lastRedeemedOnUtc: product.lastRedeemedOnUtc,
      estimatedDeliveryDateAtUtc: undefined,
      shipmentTrackingUrl: undefined,
    }))
    .sort((a, b) => {
      const dateA = a.completionDate ? new Date(a.completionDate).getTime() : 0;
      const dateB = b.completionDate ? new Date(b.completionDate).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
};

export const isListingVisibleByType = (
  listingType: string,
  areRetailProductsVisible: boolean | undefined,
  isSkinDropVisible: boolean | undefined,
): boolean => {
  if (listingType === "Product") return !!areRetailProductsVisible;
  if (listingType === "ProductFromExternalSource") return !!isSkinDropVisible;
  return true;
};

/**
 * Compute the PLP fulfillment label for a MyProducts listing.
 *
 * The label is the intersection of the per-product `fulfillmentOptions` choice and the platform's
 * pickup/shipping enablement, so the patient never sees an option their practice can't fulfill.
 * Returns `null` (no label) when the feature flag is off or when the platform offers neither
 * fulfillment method. Legacy MyProducts listings with null `fulfillmentOptions` fall back to the
 * platform pickup/shipping flags (same as PDP).
 *
 * Keep this helper in sync with the Admin form's `MyProductsFulfillmentOptions` enum copy.
 */
export const getMyProductsFulfillmentLabel = ({
  fulfillmentOptions,
  isPlatformPickupEnabled,
  isPlatformShippingEnabled,
  isFeatureFlagEnabled,
}: {
  fulfillmentOptions: Models<"DisplayRetailProductListing">["myProductsFulfillmentOptions"];
  isPlatformPickupEnabled: boolean;
  isPlatformShippingEnabled: boolean;
  isFeatureFlagEnabled: boolean;
}): string | null => {
  if (!isFeatureFlagEnabled) return null;

  const { effectivePickup, effectiveShipping } = getMyProductsEffectiveFulfillment({
    fulfillmentOptions,
    isPlatformPickupEnabled,
    isPlatformShippingEnabled,
    isFeatureFlagEnabled,
  });

  if (effectivePickup && effectiveShipping)
    return i18next.t("retailProductsHelpers.pickupOrDelivery");
  if (effectiveShipping) return i18next.t("retailProductsHelpers.shipsToYourDoor");
  if (effectivePickup) return i18next.t("retailProductsHelpers.inStorePickup");
  return null;
};

/**
 * The fulfillment line a retail listing card shows under its title: SkinDrop products ship, and a
 * MyProducts product names how the practice fulfills it (`getMyProductsFulfillmentLabel`), falling
 * back to "Shipping available" while the practice ships but that label resolves to nothing.
 */
export const getRetailProductCardFulfillmentLabel = ({
  product,
  platformSettings,
  isMyProductsShippingFlag,
}: {
  product: Pick<Models<"DisplayRetailProductListing">, "type" | "myProductsFulfillmentOptions">;
  platformSettings: Models<"PlatformSettingModel1"> | null | undefined;
  isMyProductsShippingFlag: boolean;
}): string | null => {
  if (product.type === PackageType.ProductFromExternalSource) {
    return i18next.t("retailProductsHelpers.shippedBySkinDrop");
  }
  if (product.type !== PackageType.Product) return null;

  const myProductsLabel = getMyProductsFulfillmentLabel({
    fulfillmentOptions: product.myProductsFulfillmentOptions,
    isPlatformPickupEnabled: !!platformSettings?.myProductsPickupEnabled,
    isPlatformShippingEnabled: !!platformSettings?.myProductsShippingEnabled,
    isFeatureFlagEnabled: isMyProductsShippingFlag,
  });
  if (myProductsLabel) return myProductsLabel;

  return isMyProductsShippingFlag && platformSettings?.myProductsShippingEnabled
    ? i18next.t("retailProductsHelpers.shippingAvailable")
    : null;
};

const cartContainsSkinDropLineItems = (
  lineItems: Pick<Models<"ShopDisplayOrderLineItemModel">, "displayType">[] | null | undefined,
): boolean =>
  lineItems?.some((item) => item.displayType === ShopDisplayType.ProductFromExternalSource) ??
  false;

/**
 * Shipping address country eligibility for checkout.
 *
 * The platform's Stripe region is the source of truth for where a cart can ship: a CA platform ships
 * to Canada, every other platform ships to the US. SkinDrop (ProductFromExternalSource) only fulfills
 * to the US, so a cart containing a SkinDrop line item stays US-only regardless of region.
 */
export const getAllowedShippingCountries = (
  lineItems: Pick<Models<"ShopDisplayOrderLineItemModel">, "displayType">[] | null | undefined,
  platformStripeRegion: Models<"StripeRegion">,
): ("US" | "CA")[] => {
  if (cartContainsSkinDropLineItems(lineItems)) return ["US"];
  return platformStripeRegion === "CA" ? ["CA"] : ["US"];
};

/**
 * Compute effective pickup/shipping availability for a MyProducts PDP.
 *
 * "Effective" = intersection of the per-product `fulfillmentOptions` and the platform's pickup/shipping
 * enablement. When the feature flag is off OR the listing pre-dates the flag (null `fulfillmentOptions`),
 * we fall back to platform-only flags so legacy MyProducts keep their current UX. SkinDrop callers
 * should not invoke this helper — they have their own pipeline.
 */
export const getMyProductsEffectiveFulfillment = ({
  fulfillmentOptions,
  isPlatformPickupEnabled,
  isPlatformShippingEnabled,
  isFeatureFlagEnabled,
}: {
  fulfillmentOptions: Models<"DisplayRetailProductListing">["myProductsFulfillmentOptions"];
  isPlatformPickupEnabled: boolean;
  isPlatformShippingEnabled: boolean;
  isFeatureFlagEnabled: boolean;
}): { effectivePickup: boolean; effectiveShipping: boolean } => {
  if (!isFeatureFlagEnabled || fulfillmentOptions == null) {
    return {
      effectivePickup: isPlatformPickupEnabled,
      effectiveShipping: isPlatformShippingEnabled,
    };
  }

  return {
    effectivePickup:
      (fulfillmentOptions === "PickupOnly" || fulfillmentOptions === "PickupAndShipping") &&
      isPlatformPickupEnabled,
    effectiveShipping:
      (fulfillmentOptions === "ShippingOnly" || fulfillmentOptions === "PickupAndShipping") &&
      isPlatformShippingEnabled,
  };
};
